// src/components/restricted/data-entry/DataEntryWorkspace.jsx
import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";

export default function DataEntryWorkspace() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  // State Management
  const [stagedFiles, setStagedFiles] = useState([]);
  const [selectedSource, setSelectedSource] = useState("file"); 
  const [apiEndpoint, setApiEndpoint] = useState("");

  const allowedExtensions = ["xls", "xlsx", "csv", "json", "xml", "txt"];

  // File Handlers
  const addFilesToList = (fileList) => {
    const newFiles = Array.from(fileList).filter((file) => {
      const ext = file.name.split(".").pop().toLowerCase();
      return allowedExtensions.includes(ext);
    });

    setStagedFiles((prev) => {
      const existingNames = prev.map((f) => f.name + f.size);
      const uniqueNewFiles = newFiles.filter(
        (f) => !existingNames.includes(f.name + f.size)
      );
      return [...prev, ...uniqueNewFiles];
    });
  };

  const removeFile = (index) => {
    setStagedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = () => {
    console.log("Uploading staged files:", stagedFiles);
    alert(`${stagedFiles.length} files processed for upload.`);
  };

  return (
    <main id="main" className="page-main" data-scroll-container>
      <div className="page-main-inner">
        <section className="page-section">
          <div className="container mt-4">
            
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h2 className="h4 fw-bold">Data Entry Workspace</h2>
              <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate(-1)}>
                Back to Dashboard
              </button>
            </div>

            <div className="card mb-4 shadow-sm border">
              <div className="card-header bg-light">
                <ul className="nav nav-tabs card-header-tabs">
                  <li className="nav-item">
                    <button 
                      className={`nav-link ${selectedSource === "file" ? "active" : ""}`}
                      onClick={() => setSelectedSource("file")}
                    >
                      File / Folder Upload
                    </button>
                  </li>
                  <li className="nav-item">
                    <button 
                      className={`nav-link ${selectedSource === "api" ? "active" : ""}`}
                      onClick={() => setSelectedSource("api")}
                    >
                      External API Connection
                    </button>
                  </li>
                </ul>
              </div>

              <div className="card-body">
                {selectedSource === "file" ? (
                  <div className="row g-4">
                    {/* Step 1: Selection */}
                    <div className="col-md-5 border-end">
                      <h5 className="h6 fw-bold">1. Select Data Sources</h5>
                      <p className="text-muted small">Supports XLS, CSV, JSON, XML, TXT</p>
                      
                      <div className="d-grid gap-2">
                        <button className="btn btn-primary" onClick={() => fileInputRef.current.click()}>
                          Browse Files
                        </button>
                        <button className="btn btn-outline-primary" onClick={() => folderInputRef.current.click()}>
                          Select Folder
                        </button>
                      </div>

                      <input type="file" multiple ref={fileInputRef} className="d-none" onChange={(e) => addFilesToList(e.target.files)} />
                      <input type="file" webkitdirectory="true" directory="true" ref={folderInputRef} className="d-none" onChange={(e) => addFilesToList(e.target.files)} />
                      
                      <div className="mt-4 p-3 bg-light rounded border">
                        <label className="form-label small fw-bold">Target Sustina Endpoint</label>
                        <input 
                          type="text" 
                          className="form-control form-control-sm" 
                          placeholder="https://api.sustina.com/v1/data-push"
                          value={apiEndpoint}
                          onChange={(e) => setApiEndpoint(e.target.value)}
                        />
                      </div>
                    </div>

                    {/* Step 2: Review */}
                    <div className="col-md-7">
                      <h5 className="h6 fw-bold">2. Review Staged Files ({stagedFiles.length})</h5>
                      <div className="border rounded bg-white" style={{ maxHeight: "300px", overflowY: "auto" }}>
                        {stagedFiles.length === 0 ? (
                          <p className="text-center text-muted my-5 small">No files selected yet.</p>
                        ) : (
                          <table className="table table-sm mb-0">
                            <thead className="table-light">
                              <tr>
                                <th className="ps-3">File Name</th>
                                <th>Size</th>
                                <th className="text-end pe-3">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {stagedFiles.map((f, i) => (
                                <tr key={i}>
                                  <td className="ps-3 text-truncate" style={{ maxWidth: "200px" }}>{f.name}</td>
                                  <td>{(f.size / 1024).toFixed(1)} KB</td>
                                  <td className="text-end pe-3">
                                    <button className="btn btn-link text-danger p-0" onClick={() => removeFile(i)}>
                                      &times;
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                      
                      <button 
                        className="btn btn-success w-100 mt-3" 
                        disabled={stagedFiles.length === 0}
                        onClick={handleUpload}
                      >
                        Upload All Staged Items
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="py-5 text-center">
                    <h5 className="fw-bold">API Source Configuration</h5>
                    <p className="text-muted small">Connect your system to push data directly.</p>
                    <div className="mx-auto" style={{ maxWidth: "400px" }}>
                      <input type="text" className="form-control mb-2" placeholder="Source URL" />
                      <input type="password" className="form-control mb-3" placeholder="API Key" />
                      <button className="btn btn-primary btn-sm">Test Connection</button>
                    </div>
                  </div>
                )}
              </div>
            </div>

          </div>
        </section>
      </div>
    </main>
  );
}